"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { productWithMediaDefinitionApi } from "src/services/api";
import { queryKeys } from "./queryKeys";
import {
  CreateProductWithMediaDefinitionDto,
  UpdateProductWithMediaDefinitionDto,
} from "src/services/generated-api";

interface ProductWithMediaDefinitionFilters {
  page?: number;
  limit?: number;
}

export function useProductWithMediaDefinitions(
  filters: ProductWithMediaDefinitionFilters = {}
) {
  const { page = 1, limit = 200 } = filters;
  return useQuery({
    queryKey: queryKeys.productWithMediaDefinitions.list({ page, limit }),
    queryFn: async () => {
      const res =
        await productWithMediaDefinitionApi.getManyBaseProductWithMediaDefinitionControllerProductWithMediaDefinition(
          undefined,
          undefined,
          undefined,
          undefined,
          ["createdAt,DESC"],
          undefined,
          limit,
          page - 1 // offset
        );
      return res.data;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function useProductWithMediaDefinition(id: string) {
  return useQuery({
    queryKey: queryKeys.productWithMediaDefinitions.detail(id),
    queryFn: async () => {
      const res =
        await productWithMediaDefinitionApi.getOneBaseProductWithMediaDefinitionControllerProductWithMediaDefinition(
          id,
          undefined,
          undefined,
          undefined
        );
      return res.data;
    },
    enabled: !!id,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

export function useCreateProductWithMediaDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProductWithMediaDefinitionDto) => {
      const res =
        await productWithMediaDefinitionApi.createOneBaseProductWithMediaDefinitionControllerProductWithMediaDefinition(
          data
        );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productWithMediaDefinitions.all,
      });
    },
  });
}

export function useUpdateProductWithMediaDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateProductWithMediaDefinitionDto;
    }) => {
      const res =
        await productWithMediaDefinitionApi.updateOneBaseProductWithMediaDefinitionControllerProductWithMediaDefinition(
          id,
          data
        );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productWithMediaDefinitions.all,
      });
    },
  });
}

export function useDeleteProductWithMediaDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await productWithMediaDefinitionApi.deleteOneBaseProductWithMediaDefinitionControllerProductWithMediaDefinition(
        id
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.productWithMediaDefinitions.all,
      });
    },
  });
}
